import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { parseFiltersFromUrl, serializeFiltersToUrl } from '../utils/urlFilterHelpers';
import {
  getCurrentDateRange,
  DATE_MODES,
  navigateDateRange,
  changeDateRangeMode,
  getTodayDateRange,
  getCustomDateRange,
  getAllDateRange,
  parseDateString
} from '../utils/dateUtils';
import { fetchSearchOptions, fetchFilterSuggestions, parseAiFilter } from '../api/endpointsBackend';
import { parsedPeriod, periodToDateRange } from '../utils/aiPeriod';
import { readTabState, writeTabState } from '../utils/tabStorage';
import useIsPhone from '../hooks/useIsPhone';

const FilterContext = createContext();

const STORAGE_KEY = 'mikapital_filters';

// Routes where global filter state is mirrored to the URL. Other pages keep
// a clean URL; the filter state stays in context so it is restored on return.
const FILTER_AWARE_ROUTES = ['/movements', '/dashboard'];

// On the phone, Home keeps its own month, so Movements' filters never land in
// its URL.
const isFilterAwareRoute = (pathname, isPhone) =>
  FILTER_AWARE_ROUTES.includes(pathname) && !(isPhone && pathname === '/dashboard');

/**
 * Check if two date ranges are equal
 */
const areDateRangesEqual = (a, b) => {
  if (!a || !b) return false;
  return a.start_date === b.start_date &&
         a.end_date === b.end_date &&
         a.mode === b.mode;
};

/**
 * FilterProvider - Global filter state management
 *
 * selectedFilters is an array of { entity, column, operator, values }
 * matching the API format directly.
 */
export function FilterProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(location.pathname);

  const isNavigatingRef = useRef(false);
  const isPhone = useIsPhone();
  const isPhoneRef = useRef(isPhone);
  isPhoneRef.current = isPhone;

  const [state, setState] = useState(() => {
    const defaultDateRange = getCurrentDateRange(DATE_MODES.MONTH);

    // 1. Try URL first (for shared links)
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = parseFiltersFromUrl(searchParams);

    const hasUrlFilters = urlFilters.filters.length > 0;
    const hasUrlGroups = urlFilters.filterGroups?.length > 0;
    const hasUrlDate = !areDateRangesEqual(urlFilters.dateRange, defaultDateRange);

    if (hasUrlFilters || hasUrlGroups || hasUrlDate) {
      return {
        dateRange: urlFilters.dateRange,
        selectedFilters: urlFilters.filters,
        filterGroups: urlFilters.filterGroups || [],
        groupsOperator: 'and',
        isAdvancedMode: hasUrlGroups,
        filterOptions: null,
        timePresets: [],
        loading: true,
        filterOptionsLoading: false
      };
    }

    // 2. Try storage: this tab's own, else the last used in any tab (MI-326)
    try {
      const stored = readTabState(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only accept array format; discard old object format
        const filters = Array.isArray(parsed.selectedFilters) ? parsed.selectedFilters : [];
        const groups = Array.isArray(parsed.filterGroups) ? parsed.filterGroups : [];
        return {
          dateRange: parsed.dateRange || defaultDateRange,
          selectedFilters: filters,
          filterGroups: groups,
          groupsOperator: parsed.groupsOperator || 'and',
          isAdvancedMode: parsed.isAdvancedMode || false,
          filterOptions: null,
          timePresets: [],
          loading: true,
          filterOptionsLoading: false
        };
      }
    } catch (e) {
      console.error('Failed to parse stored filters:', e);
    }

    // 3. Default values
    return {
      dateRange: defaultDateRange,
      selectedFilters: [],
      filterGroups: [],
      groupsOperator: 'and',
      isAdvancedMode: false,
      filterOptions: null,
      timePresets: [],
      loading: true,
      filterOptionsLoading: false
    };
  });

  // The popstate listener is registered once, so it reads the current
  // filters through a ref.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Persist to storage whenever filters change
  useEffect(() => {
    const toStore = {
      dateRange: state.dateRange,
      selectedFilters: state.selectedFilters,
      filterGroups: state.filterGroups,
      groupsOperator: state.groupsOperator,
      isAdvancedMode: state.isAdvancedMode
    };
    writeTabState(STORAGE_KEY, JSON.stringify(toStore));
  }, [state.dateRange, state.selectedFilters, state.filterGroups, state.groupsOperator, state.isAdvancedMode]);

  // Sync to URL (only on filter-aware routes; other pages keep a clean URL)
  useEffect(() => {
    // A back or forward swipe that lands on another page. If that entry has
    // no filters in its URL, they are written over it: a push here would
    // drop every forward entry.
    const poppedHere = navigationType === 'POP' && prevPathRef.current !== location.pathname;
    prevPathRef.current = location.pathname;

    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    if (!isFilterAwareRoute(location.pathname, isPhone)) return;

    const searchString = serializeFiltersToUrl(
      state.selectedFilters,
      state.dateRange,
      state.filterGroups
    );
    // A back navigation that changes only the pathname lands here with the
    // URL already right; writing it again would push a duplicate entry.
    if (searchString === window.location.search.replace(/^\?/, '')) return;
    // On the phone each period tap replaces the entry, so the back swipe
    // leaves the page (or closes a sheet) instead of stepping through months.
    navigate({ search: searchString }, { replace: isPhone || poppedHere });
  // navigationType is read, not a trigger: it changes with the pathname.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedFilters, state.dateRange, state.filterGroups, location.pathname, navigate, isPhone]);

  // Load filter options from backend
  useEffect(() => {
    const loadOptions = async () => {
      setState(prev => ({ ...prev, filterOptionsLoading: true }));

      try {
        const options = await fetchSearchOptions(
          state.dateRange?.start_date,
          state.dateRange?.end_date
        );
        setState(prev => ({
          ...prev,
          filterOptions: options.filters || [],
          timePresets: options.timePresets || [],
          loading: false,
          filterOptionsLoading: false
        }));
      } catch (err) {
        console.error('Failed to load filter options:', err);
        setState(prev => ({ ...prev, loading: false, filterOptionsLoading: false }));
      }
    };
    loadOptions();
  }, [state.dateRange?.start_date, state.dateRange?.end_date]);

  // Browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Off the filter-aware routes the URL carries no filters, and a sheet
      // closing there (a popstate too) must not reset them.
      if (!isFilterAwareRoute(window.location.pathname, isPhoneRef.current)) return;

      const searchParams = new URLSearchParams(window.location.search);
      const parsed = parseFiltersFromUrl(searchParams);
      const groups = parsed.filterGroups || [];

      // Compare parsed filter state, not the raw search: movement_id and the
      // Display params share it and change without the filters changing.
      const current = stateRef.current;
      const unchanged =
        serializeFiltersToUrl(parsed.filters, parsed.dateRange, groups) ===
        serializeFiltersToUrl(current.selectedFilters, current.dateRange, current.filterGroups);
      if (unchanged) return;

      // Set only together with the setState below: armed without it, the flag
      // would swallow the next real filter change's URL write.
      isNavigatingRef.current = true;
      setState(prev => ({
        ...prev,
        dateRange: parsed.dateRange,
        selectedFilters: parsed.filters,
        filterGroups: groups,
        isAdvancedMode: groups.length > 0 ? true : prev.isAdvancedMode
      }));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Build API filters — just filter out entries with empty values
  const apiFilters = useMemo(() => {
    return state.selectedFilters.filter(f => f.values && f.values.length > 0);
  }, [state.selectedFilters]);

  // Legacy compatibility: expose currentMonth
  const currentMonth = useMemo(() => {
    const ref = state.dateRange?.reference;
    if (ref && typeof ref.year === 'number' && typeof ref.month === 'number') {
      return { year: ref.year, month: ref.month };
    }
    if (state.dateRange?.start_date) {
      const [year, month] = state.dateRange.start_date.split('-').map(Number);
      return { year, month: month - 1 };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  }, [state.dateRange]);

  // Date range actions
  const setDateRange = useCallback((dateRange) => {
    setState(prev => ({ ...prev, dateRange }));
  }, []);

  const navigatePeriod = useCallback((direction) => {
    setState(prev => ({
      ...prev,
      dateRange: navigateDateRange(prev.dateRange, direction)
    }));
  }, []);

  const setDateMode = useCallback((mode) => {
    setState(prev => ({
      ...prev,
      dateRange: changeDateRangeMode(prev.dateRange, mode)
    }));
  }, []);

  const goToToday = useCallback(() => {
    setState(prev => ({
      ...prev,
      dateRange: getTodayDateRange(prev.dateRange.mode)
    }));
  }, []);

  const setCustomRange = useCallback((startDate, endDate) => {
    setState(prev => ({
      ...prev,
      dateRange: getCustomDateRange(startDate, endDate)
    }));
  }, []);

  // Filter actions
  const addFilter = useCallback((filter) => {
    setState(prev => {
      const idx = prev.selectedFilters.findIndex(
        f => f.entity === filter.entity && f.column === filter.column && f.operator === filter.operator
      );
      // If values are empty, remove the filter entirely
      if (!filter.values || filter.values.length === 0) {
        if (idx >= 0) {
          return { ...prev, selectedFilters: prev.selectedFilters.filter((_, i) => i !== idx) };
        }
        return prev;
      }
      if (idx >= 0) {
        // Replace existing
        const updated = [...prev.selectedFilters];
        updated[idx] = filter;
        return { ...prev, selectedFilters: updated };
      }
      return { ...prev, selectedFilters: [...prev.selectedFilters, filter] };
    });
  }, []);

  const appendFilter = useCallback((filter) => {
    if (!filter.values || filter.values.length === 0) return;
    setState(prev => ({
      ...prev,
      selectedFilters: [...prev.selectedFilters, filter]
    }));
  }, []);

  const removeFilter = useCallback((index) => {
    setState(prev => ({
      ...prev,
      selectedFilters: prev.selectedFilters.filter((_, i) => i !== index)
    }));
  }, []);

  const updateFilter = useCallback((index, updates) => {
    setState(prev => {
      const updated = [...prev.selectedFilters];
      updated[index] = { ...updated[index], ...updates };
      return { ...prev, selectedFilters: updated };
    });
  }, []);

  /**
   * Apply a row picked from the value-first menu.
   *
   * Every pick is its own chip, even on a field that already has one — the
   * same rule Linear follows. Two chips on one field AND together ("tagged
   * Bug AND tagged Feature"); to say "either", put both values in one chip
   * from its own value list.
   */
  const applySuggestion = useCallback((suggestion) => {
    const condition = suggestion?.condition;
    if (!condition?.values?.length) return;
    setState(prev => {
      const duplicate = prev.selectedFilters.some(
        f => f.entity === condition.entity
          && f.column === condition.column
          && f.operator === condition.operator
          && (f.values || []).length === 1
          && String(f.values[0]) === String(condition.values[0])
      );
      // Re-picking the exact same value is a no-op rather than a second
      // identical chip that narrows nothing.
      if (duplicate) return prev;
      return { ...prev, selectedFilters: [...prev.selectedFilters, condition] };
    });
  }, []);

  const removeLastFilter = useCallback(() => {
    setState(prev => (
      prev.selectedFilters.length
        ? { ...prev, selectedFilters: prev.selectedFilters.slice(0, -1) }
        : prev
    ));
  }, []);

  const clearFilters = useCallback(() => {
    setState(prev => ({ ...prev, selectedFilters: [], filterGroups: [], groupsOperator: 'and', isAdvancedMode: false }));
  }, []);

  /**
   * Replace the filters and the period with one search: what the chat's "see
   * all in Movements" link opens (MI-343). The filters come in the shape they
   * are kept in here, and a search with no dates is one over all time. One
   * update, so the list loads once.
   */
  const showSearch = useCallback(({ filters = [], start = null, end = null }) => {
    setState(prev => ({
      ...prev,
      selectedFilters: filters,
      filterGroups: [],
      groupsOperator: 'and',
      isAdvancedMode: false,
      dateRange: start && end
        ? getCustomDateRange(parseDateString(start), parseDateString(end))
        : getAllDateRange(),
    }));
  }, []);

  const refreshFilterOptions = useCallback(async () => {
    setState(prev => ({ ...prev, filterOptionsLoading: true }));
    try {
      const options = await fetchSearchOptions(
        state.dateRange?.start_date,
        state.dateRange?.end_date
      );
      setState(prev => ({
        ...prev,
        filterOptions: options.filters || [],
        timePresets: options.timePresets || [],
        filterOptionsLoading: false
      }));
    } catch (err) {
      console.error('Failed to refresh filter options:', err);
      setState(prev => ({ ...prev, filterOptionsLoading: false }));
    }
  }, [state.dateRange?.start_date, state.dateRange?.end_date]);

  // Advanced mode actions
  const enableAdvancedMode = useCallback(() => {
    setState(prev => ({ ...prev, isAdvancedMode: true }));
  }, []);

  const disableAdvancedMode = useCallback(() => {
    setState(prev => ({ ...prev, isAdvancedMode: false, filterGroups: [], groupsOperator: 'and' }));
  }, []);

  const toggleGroupsOperator = useCallback(() => {
    setState(prev => ({
      ...prev,
      groupsOperator: prev.groupsOperator === 'and' ? 'or' : 'and'
    }));
  }, []);

  const addFilterGroup = useCallback(() => {
    setState(prev => ({
      ...prev,
      filterGroups: [...prev.filterGroups, { id: String(Date.now()), operator: 'or', conditions: [] }]
    }));
  }, []);

  const removeFilterGroup = useCallback((groupId) => {
    setState(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.filter(g => g.id !== groupId)
    }));
  }, []);

  const toggleGroupOperator = useCallback((groupId) => {
    setState(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId ? { ...g, operator: g.operator === 'and' ? 'or' : 'and' } : g
      )
    }));
  }, []);

  const addGroupCondition = useCallback((groupId, condition) => {
    setState(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g => {
        if (g.id !== groupId) return g;
        // Same dedup logic as addFilter
        const idx = g.conditions.findIndex(
          c => c.entity === condition.entity && c.column === condition.column && c.operator === condition.operator
        );
        if (!condition.values || condition.values.length === 0) {
          if (idx >= 0) {
            return { ...g, conditions: g.conditions.filter((_, i) => i !== idx) };
          }
          return g;
        }
        if (idx >= 0) {
          const updated = [...g.conditions];
          updated[idx] = condition;
          return { ...g, conditions: updated };
        }
        return { ...g, conditions: [...g.conditions, condition] };
      })
    }));
  }, []);

  const removeGroupCondition = useCallback((groupId, conditionIndex) => {
    setState(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId
          ? { ...g, conditions: g.conditions.filter((_, i) => i !== conditionIndex) }
          : g
      )
    }));
  }, []);

  // Computed: API-ready filter groups (filter out empty conditions/groups)
  const apiFilterGroups = useMemo(() => {
    if (!state.isAdvancedMode) return [];
    return state.filterGroups
      .map(g => ({
        ...g,
        conditions: g.conditions.filter(c => c.values && c.values.length > 0)
      }))
      .filter(g => g.conditions.length > 0);
  }, [state.isAdvancedMode, state.filterGroups]);

  /**
   * Ask the server which values match what is being typed, and how many
   * movements each would leave. Scoped to the same period and filters the list
   * is already showing.
   */
  const fetchSuggestions = useCallback(async (query, { signal } = {}) => {
    return fetchFilterSuggestions(query, {
      filters: apiFilters,
      groups: apiFilterGroups,
      groupsOperator: state.groupsOperator,
      start_date: state.dateRange?.start_date,
      end_date: state.dateRange?.end_date,
      signal,
    });
  }, [apiFilters, apiFilterGroups, state.groupsOperator, state.dateRange]);

  /**
   * Turn free text into chips. Returns false when nothing could be resolved, so
   * the menu can say so instead of closing on an empty result.
   */
  const applyAiFilter = useCallback(async (query) => {
    const result = await parseAiFilter(query, {
      start_date: state.dateRange?.start_date,
      end_date: state.dateRange?.end_date,
    });
    const conditions = result?.filter_group?.conditions || [];
    const range = parsedPeriod(result);
    if (!conditions.length && !range) return false;

    setState(prev => {
      const next = { ...prev };
      if (conditions.length) {
        // Conditions land as ordinary, visible, removable chips — the AI never
        // rewrites the query behind the user's back.
        next.selectedFilters = [...prev.selectedFilters, ...conditions];
      }
      if (range) {
        next.dateRange = periodToDateRange(range);
      }
      return next;
    });
    return true;
  }, [state.dateRange]);

  /**
   * A period written the way people say it — "June 2024", "last week of
   * december", "abril 2023 to june 3". The same parser the AI filter uses
   * already returns a date_range; here only that half is applied, so typing a
   * period changes the period and adds no chips.
   */
  const applyAiPeriod = useCallback(async (query) => {
    const range = parsedPeriod(await parseAiFilter(query));
    if (!range) return false;
    setState(prev => ({ ...prev, dateRange: periodToDateRange(range) }));
    return true;
  }, []);

  const value = {
    // State
    dateRange: state.dateRange,
    selectedFilters: state.selectedFilters,
    filterGroups: state.filterGroups,
    groupsOperator: state.groupsOperator,
    isAdvancedMode: state.isAdvancedMode,
    filterOptions: state.filterOptions,
    filterOptionsLoading: state.filterOptionsLoading,
    timePresets: state.timePresets,
    apiFilters,
    apiFilterGroups,
    loading: state.loading,

    // Legacy
    currentMonth,

    // Date range actions
    setDateRange,
    navigatePeriod,
    setDateMode,
    goToToday,
    setCustomRange,

    // Filter actions
    addFilter,
    appendFilter,
    applySuggestion,
    removeFilter,
    removeLastFilter,
    fetchSuggestions,
    applyAiFilter,
    applyAiPeriod,
    updateFilter,
    clearFilters,
    showSearch,
    refreshFilterOptions,

    // Advanced mode actions
    enableAdvancedMode,
    disableAdvancedMode,
    addFilterGroup,
    removeFilterGroup,
    toggleGroupOperator,
    toggleGroupsOperator,
    addGroupCondition,
    removeGroupCondition
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
}

/**
 * useFilters - Hook to access global filter state
 */
export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within FilterProvider');
  }
  return context;
}
